// Fill the sign-up / login form from the phone itself — WITHOUT reading the
// phone book (decided 2026-10-01). Two zero-permission sources:
//
//   1. Android/Google AUTOFILL — the form's fields carry `autocomplete` hints
//      (AUTOFILL below), so the system offers the user's saved name, e-mail and
//      number above the keyboard. Pure web; works after an OTA.
//   2. Google's PHONE NUMBER HINT — a one-tap sheet listing this phone's SIM
//      number(s) (PhoneHintPlugin.java, Identity.getSignInClient().
//      getPhoneNumberHintIntent). No permission; native, so APK only.
//
// ⚠️ NOT READ_CONTACTS. The phone book holds OTHER people; there is no reliable
// "me" card, Play restricts the permission, and a contacts prompt on the first
// screen of a home-automation app reads as a data grab.
//
// ⚠️ Both sources hand back numbers like "+91 77900 03000". Every mobile field
// must go through mobile10() — slicing the FIRST ten digits gave "9177900030",
// a different person's number.

import { registerPlugin } from '@capacitor/core';
import { isNativePlatform } from '../platform';

/** Indian mobile -> its 10 digits: "+91 77900 03000", "0917790003000" -> "7790003000". */
export function mobile10(value) {
  const d = String(value ?? '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
}

/** `autocomplete` hints for each form field (HTML autofill tokens). */
export const AUTOFILL = {
  firstName: 'given-name',
  middleName: 'additional-name',
  lastName: 'family-name',
  email: 'email',
  mobile: 'tel-national',
};

const PhoneHint = registerPlugin('PhoneHint');

/**
 * Ask Google for this phone's number. -> '7790003000', or '' when there is
 * none, the user closed the sheet, or this is not the Android app.
 * Never throws.
 */
export async function pickPhoneNumber() {
  if (!isNativePlatform()) return '';
  try {
    // NB: the plugin is CALLED, never returned from an async function — a
    // Capacitor plugin is a thenable Proxy (see CLAUDE.md).
    const r = await PhoneHint.pick();
    return mobile10(r && r.phoneNumber);
  } catch (e) {
    return '';
  }
}

/**
 * A mobile field's onChange: typing is capped at 10 digits (an 11th keystroke is
 * ignored), while a PASTE or AUTOFILL — more than one character arriving at
 * once — keeps the LAST 10 digits, so "+91 77900 03000" lands as 7790003000.
 * ⚠️ So the field must NOT carry maxLength: the browser applies it to autofill
 * too, and would cut that same value to "+91 77900 " before this ever ran.
 */
export function mobileInput(raw, prev = '') {
  const d = String(raw ?? '').replace(/\D/g, '');
  if (d.length <= 10) return d;
  return String(raw).length - String(prev).length > 1 ? d.slice(-10) : String(prev);
}
